import { onScopeDispose } from "vue";
import { PiniaColadaQueryHooksPlugin, type PiniaColadaOptions, type PiniaColadaPlugin, type QueryCache } from "@pinia/colada";
import { PiniaColadaDelay } from "@pinia/colada-plugin-delay";

import { Unauthorized } from "@tmi/types/api/responses";
import pinia from "@/plugins/PiniaPlugin";
import { clientStateManager } from "@/services/error-services";
import { AUTH_RESET_EVENT, useAuthStore } from "@/stores/Auth";
import { checkSessionWithCache } from "@/utils/network-utils";

/**
 * App-wide Pinia Colada configuration, installed by main.ts.
 *
 * Colada's own defaults (5 s staleTime, refetch on every window focus) refetched the dashboard watchlist
 * each time a member alt-tabbed back, and each refetch blanked the card. Colada serves slow-moving member
 * data (today, the watchlist), so data stays fresh for 60 s, stays cached for 30 min, and a focus change
 * never refetches on its own. A query that changes more slowly sets a longer staleTime itself.
 */
export const QUERY_DEFAULTS = {
    staleTime: 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
} as const;

/**
 * Signing out does not reload the page, so the query cache outlives it, and with the staleTime above the
 * next member to sign in on this tab would be shown the previous member's watchlist. Auth.reset() announces
 * every sign-out; empty every query then.
 *
 * Entries are reset in place, never removed. The sign-out event fires while the dashboard is still mounted,
 * and a mounted useQuery keeps its entry object. A removed entry lived on in that component, and when it
 * unmounted its 30-minute GC timer deleted by key whatever entry the next member's watchlist had by then, so
 * optimistic add/remove and the refetch after them no longer reached the table.
 */
export function resetQueryCache( queryCache: QueryCache ) {
    queryCache.getEntries().forEach( entry => {
        queryCache.cancel( entry );
        queryCache.setEntryState( entry, { status: 'pending', data: undefined, error: null } );
        entry.placeholderData = null;
        // setEntryState stamps `when` with now; 0 marks the entry stale so the next mount fetches
        entry.when = 0;
    } );
}

export const PiniaColadaAuthReset: PiniaColadaPlugin = ( { queryCache, scope } ) => {
    if ( typeof window === 'undefined' ) {
        return;
    }

    const onAuthReset = () => resetQueryCache( queryCache );
    scope.run( () => {
        window.addEventListener( AUTH_RESET_EVENT, onAuthReset );
        onScopeDispose( () => window.removeEventListener( AUTH_RESET_EVENT, onAuthReset ) );
    } );
};

/** mande rejects a non-2xx answer with an Error that carries the Response. */
export function isUnauthorizedResponse( error: unknown ): boolean {
    return ( error as { response?: { status?: unknown } } | null )?.response?.status === Unauthorized.statusCode;
}

/**
 * UX roadmap #16: the queries and mutations here call mande, not goFetch, so a 401 for an expired session only ever
 * showed as a failed watchlist ("Oops", an add that did nothing). Same rule as goFetch: the session ends only once the
 * server confirms it is gone (a 401 for a live session is about the resource), through the same main-thread handler.
 * Never rejects.
 */
export async function reportRejectedSession( error: unknown ): Promise< void > {
    if ( !isUnauthorizedResponse( error ) ) {
        return;
    }

    const authToken: string | null = useAuthStore( pinia ).authToken;
    if ( !authToken || await checkSessionWithCache( authToken ) ) {
        return;
    }

    clientStateManager.queueClientError( new Unauthorized(), { request: 'pinia-colada' } );
}

// Not returned: Colada awaits a hook before the query's own onSettled and the mutation's own onError.
function onColadaError( error: unknown ): void {
    void reportRejectedSession( error );
}

export const piniaColadaOptions: PiniaColadaOptions = {
    queryOptions: { ...QUERY_DEFAULTS },
    mutationOptions: {
        onError: onColadaError
    },
    plugins: [
//         PiniaColadaAutoRefetch( { autoRefetch: true } ),
        // asyncStatus only turns 'loading' after 200 ms, so a quick refetch never dims anything
        PiniaColadaDelay( { delay: 200 } ),
        PiniaColadaAuthReset,
        PiniaColadaQueryHooksPlugin( { onError: onColadaError } )
    ]
};
